import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { FileText, Users, Wallet, ArrowUp, ArrowDown } from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('th-TH');
const fmtDate = (d) => new Date(d).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' });

export default function History() {
  const [tab, setTab] = useState('debtors'); // 'debtors' | 'mydebt'
  const [data, setData] = useState([]);
  const [myData, setMyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [d, m] = await Promise.all([api.get('/history/debtors'), api.get('/history/mydebt')]);
        setData(d.data);
        setMyData(m.data);
      } catch { }
      setLoading(false);
    };
    load();
  }, []);

  const current = tab === 'debtors' ? data : myData;
  const nameKey = tab === 'debtors' ? 'name' : 'creditorName';
  const filtered = selected ? current.filter(d => d._id === selected) : current;

  const grandTotal = current.reduce((s, d) => s + (d.totalDebt || 0), 0);
  const grandAdded = current.reduce((s, d) => s + (d.totalAdded || 0), 0);
  const grandPaid = current.reduce((s, d) => s + (d.totalPaid || 0), 0);

  const exportPDF = async () => {
    setExporting(true);
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);

      const title = tab === 'debtors' ? 'ລາຍງານລູກໜີ້' : 'ລາຍງານໜີ້ຂ້ອຍ';
      const exportDate = new Date().toLocaleDateString('th-TH');

      const rows = [];
      filtered.forEach(d => {
        (d.transactions || []).forEach(tx => {
          rows.push({
            name: d[nameKey],
            amount: tx.type === 'add' ? `+${fmt(tx.amount)}` : `-${fmt(tx.amount)}`,
            type: tx.type === 'add' ? 'ໜີ້' : 'ຊຳລະ',
            method: tx.paymentMethod === 'cash' ? 'ເງິນສົດ' : 'ໂອນ',
            date: fmtDate(tx.date),
            note: tx.note || '',
            by: tx.createdBy,
            isAdd: tx.type === 'add',
          });
        });
      });

      const el = document.createElement('div');
      el.style.cssText = [
        'position:fixed', 'left:-9999px', 'top:0', 'width:780px',
        'background:#fff', 'padding:28px 32px', 'color:#111',
        'font-family:"Noto Sans Lao","Phetsarath OT","Saysettha OT",Helvetica,sans-serif',
        'font-size:13px', 'line-height:1.6',
      ].join(';');

      el.innerHTML = `
        <div style="margin-bottom:16px;">
          <div style="font-size:20px;font-weight:700;margin-bottom:4px;">${title}</div>
          <div style="color:#666;font-size:11px;">ວັນທີ Export: ${exportDate}</div>
        </div>
        <div style="display:flex;gap:32px;margin-bottom:20px;padding:12px 16px;background:#f5f5f0;border-radius:8px;">
          <div><div style="font-size:11px;color:#888;margin-bottom:2px;">ໜີ້ທັງໝົດ</div><div style="font-weight:700;color:#c0392b;">KIP ${fmt(selected ? current.find(d => d._id === selected)?.totalAdded : grandAdded)}</div></div>
          <div><div style="font-size:11px;color:#888;margin-bottom:2px;">ຊຳລະແລ້ວ</div><div style="font-weight:700;color:#27ae60;">KIP ${fmt(selected ? current.find(d => d._id === selected)?.totalPaid : grandPaid)}</div></div>
          <div><div style="font-size:11px;color:#888;margin-bottom:2px;">ຄົງເຫຼືອ</div><div style="font-weight:700;color:#e67e22;">KIP ${fmt(selected ? current.find(d => d._id === selected)?.totalDebt : grandTotal)}</div></div>
        </div>
        <table style="width:100%;border-collapse:collapse;font-size:11px;">
          <thead>
            <tr style="background:#3e3f29;color:#fff;">
              <th style="padding:8px 10px;text-align:left;">ຊື່</th>
              <th style="padding:8px 10px;text-align:right;">ຈຳນວນ</th>
              <th style="padding:8px 10px;text-align:center;">ປະເພດ</th>
              <th style="padding:8px 10px;text-align:center;">ວິທີ</th>
              <th style="padding:8px 10px;text-align:center;">ວັນທີ</th>
              <th style="padding:8px 10px;text-align:left;">ໝາຍເຫດ</th>
              <th style="padding:8px 10px;text-align:left;">ບັນທຶກໂດຍ</th>
            </tr>
          </thead>
          <tbody>
            ${rows.map((r, i) => `
              <tr style="background:${i % 2 === 0 ? '#fff' : '#f9f8f2'};">
                <td style="padding:7px 10px;">${r.name}</td>
                <td style="padding:7px 10px;text-align:right;font-weight:600;color:${r.isAdd ? '#c0392b' : '#27ae60'};">${r.amount}</td>
                <td style="padding:7px 10px;text-align:center;">${r.type}</td>
                <td style="padding:7px 10px;text-align:center;">${r.method}</td>
                <td style="padding:7px 10px;text-align:center;">${r.date}</td>
                <td style="padding:7px 10px;">${r.note}</td>
                <td style="padding:7px 10px;">${r.by}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;

      document.body.appendChild(el);

      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      document.body.removeChild(el);

      const imgData = canvas.toDataURL('image/png');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const imgH = (canvas.height * pageW) / canvas.width;

      let y = 0;
      while (y < imgH) {
        if (y > 0) doc.addPage();
        doc.addImage(imgData, 'PNG', 0, -y, pageW, imgH);
        y += pageH;
      }

      doc.save(`debt-history-${tab}-${Date.now()}.pdf`);
    } catch (e) { console.error(e); }
    setExporting(false);
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="pt-2 flex items-center justify-between">
        <div>
          <h1 className="page-title">ປະຫວັດ</h1>
          <p className="text-muted text-xs">ຂໍ້ມູນທັງໝົດ</p>
        </div>
        <button onClick={exportPDF} disabled={exporting}
          className="btn-secondary text-xs px-3 py-2 flex items-center gap-1.5">
          {exporting ? <div className="w-3 h-3 border-2 border-dark border-t-transparent rounded-full animate-spin" /> : <FileText size={14} />}
          Export PDF
        </button>
      </div>

      {/* Tab */}
      <div className="grid grid-cols-2 gap-2 bg-dark/10 p-1 rounded-xl">
        {[
          { key: 'debtors', icon: <Users size={14} />, label: 'ລູກໜີ້' },
          { key: 'mydebt', icon: <Wallet size={14} />, label: 'ໜີ້ຂ້ອຍ' }
        ].map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setSelected(null); }}
            className={`py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1.5
              ${tab === t.key ? 'bg-dark text-cream shadow-sm' : 'text-muted'}`}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center pt-8">
          <div className="w-7 h-7 border-2 border-dark border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="card bg-gradient-to-br from-dark to-dark/80 border-0">
            <p className="text-cream/50 text-xs mb-2">{selected ? current.find(d => d._id === selected)?.[nameKey] : 'ພາບລວມທັງໝົດ'}</p>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <p className="text-cream/40 text-xs">ໜີ້ທັງໝົດ</p>
                <p className="text-red-400 font-bold text-sm">{fmt(selected ? current.find(d => d._id === selected)?.totalAdded : grandAdded)} KIP</p>
              </div>
              <div>
                <p className="text-cream/40 text-xs">ຊຳລະແລ້ວ</p>
                <p className="text-green-400 font-bold text-sm">{fmt(selected ? current.find(d => d._id === selected)?.totalPaid : grandPaid)} KIP</p>
              </div>
              <div>
                <p className="text-cream/40 text-xs">ຄົງເຫຼືອ</p>
                <p className="text-sand font-bold text-sm">{fmt(selected ? current.find(d => d._id === selected)?.totalDebt : grandTotal)} KIP</p>
              </div>
            </div>
          </div>

          {/* Filter by name */}
          <div>
            <p className="text-xs text-muted mb-2">ກອງຕາມຊື່</p>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setSelected(null)}
                className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all
                  ${!selected ? 'bg-dark text-cream' : 'bg-white/60 text-muted'}`}>
                ທັງໝົດ
              </button>
              {current.map(d => (
                <button key={d._id} onClick={() => setSelected(d._id)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all
                    ${selected === d._id ? 'bg-dark text-cream' : 'bg-white/60 text-muted'}`}>
                  {d[nameKey]}
                </button>
              ))}
            </div>
          </div>

          {/* Transaction list */}
          <div className="space-y-2">
            {filtered.map(d => (
              <div key={d._id} className="space-y-1.5">
                <div className="flex items-center justify-between px-1">
                  <p className="font-semibold text-dark text-sm">{d[nameKey]}</p>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold ${(d.totalDebt || 0) > 0 ? 'text-red-500' : 'text-green-500'}`}>
                      {fmt(d.totalDebt)} KIP
                    </span>
                    {!d.isActive && <span className="text-xs bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded">ປິດແລ້ວ</span>}
                  </div>
                </div>
                {(d.transactions || []).length === 0 ? (
                  <div className="card py-3 text-center"><p className="text-muted text-xs">ບໍ່ມີລາຍການ</p></div>
                ) : (
                  [...d.transactions].reverse().map((tx, i) => (
                    <div key={tx._id} className="card flex items-center gap-2.5 py-2.5 animate-slide-up"
                      style={{ animationDelay: `${i * 20}ms` }}>
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0
                        ${tx.type === 'add' ? 'bg-red-100 text-red-500' : 'bg-green-100 text-green-600'}`}>
                        {tx.type === 'add' ? <ArrowUp size={13} /> : <ArrowDown size={13} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`font-bold text-xs ${tx.type === 'add' ? 'text-red-500' : 'text-green-600'}`}>
                            {tx.type === 'add' ? '+' : '-'}{fmt(tx.amount)} KIP
                          </span>
                          {tx.paymentMethod === 'cash'
                            ? <span className="badge-green" style={{ fontSize: '10px' }}>ເງິນສົດ</span>
                            : <span className="badge-rainbow">ໂອນ</span>}
                        </div>
                        {tx.note && <p className="text-xs text-muted truncate">{tx.note}</p>}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-muted">{fmtDate(tx.date)}</p>
                        <p className="text-xs text-muted/50">{tx.createdBy}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
